import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _omit from 'lodash/omit';

import DeviceProps from '@magnus/react-native-device-props';

import Billing from '@web-solutions/module-billing';

import { PaymentSystem } from '@web-solutions/react-billing';

import {
  selectPaymentProject,
  selectPaymentSystem,
} from '@web-solutions/core/store/billing/selectors';

import { getVATMetadata } from 'core/utils/get-vat-metadata';

import { setOrderPending } from 'core/store/billing/actions';

import type { OneTimePaymentProps } from '..';

type Params = Pick<
  OneTimePaymentProps,
  'activeProduct' | 'oneTimeProducts' | 'trigger' | 'metadata' | 'solidMetadata'
>;

export const useCreateOrdersProcessor = ({
  activeProduct,
  oneTimeProducts,
  trigger,
  metadata,
  solidMetadata,
}: Params) => {
  const dispatch = useDispatch<any>();

  const [orderDetails, setOrderDetails] = useState(null);

  const email = useSelector((state: any) => state.profile.email);
  const countryCode = useSelector((state: any) => state.billing.countryCode);

  const paymentSystem = useSelector(selectPaymentSystem);
  const paymentProject = useSelector(selectPaymentProject);

  const createOrders = useCallback(async () => {
    // Stripe has no /orders endpoint — onetime products come from GET /stripe/products?type=onetime
    // and the purchase is made directly via POST /stripe/one-time-payment on submit.
    if (paymentSystem === PaymentSystem.STRIPE) {
      return;
    }

    const idfm = await DeviceProps.getIDFM();
    try {
      dispatch(setOrderPending(true));

      setOrderDetails(null);

      if (!activeProduct && !oneTimeProducts) return;
      const productWithOD = activeProduct
        ? {
            id: activeProduct.priceText,
            priceText: activeProduct.amount,
            amount: activeProduct.amount,
            currency: activeProduct.currency,
            product: activeProduct.productCode,
            isOneTimePurchase: true,
            order_description: idfm,
            ...(activeProduct?.trigger && { trigger: activeProduct.trigger })
          }
        : null;
      const productsWithOD = oneTimeProducts?.map((item) => ({
        ..._omit(item, ['customTitle', 'customSubTitle']),
        order_description: idfm,
      }));

      const res = await Billing.createOrder({
        orderDescription: idfm,
        one_time_product: productWithOD || productsWithOD?.[0],
        one_time_products: productsWithOD || [],
        trigger,
        metadata: {
          ...(metadata ? metadata : {}),
          ...getVATMetadata({
            countryCode,
          }),
        },
        solidMetadata,
        paymentSystem,
        paymentProject,
      } as any);

      setOrderDetails(res as any);
    } catch (error) {
      console.warn('[ERROR CREATE ORDER]', error);
    } finally {
      dispatch(setOrderPending(false));
    }
  }, [
    dispatch,
    activeProduct,
    oneTimeProducts,
    email,
    trigger,
    metadata,
    countryCode,
    solidMetadata,
    paymentSystem,
  ]);

  return {
    orderDetails,
    createOrders,
  };
};
